<template>
  <div class="candidatos-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-users"></i>
        Gestão de Candidatos
      </h1>
      <p class="page-subtitle">Gerencie todos os candidatos cadastrados no sistema</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Buscar</label>
          <input 
            v-model="filters.search" 
            type="text" 
            placeholder="Nome, CPF ou E-mail"
            class="form-control"
            @input="debounceSearch"
          />
        </div>
        
        <div class="filter-group">
          <label>Status</label>
          <select v-model="filters.status" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="A">Ativo</option>
            <option value="I">Inativo</option>
          </select>
        </div>

        <div class="filter-group">
          <label>UF</label>
          <select v-model="filters.uf" class="form-control" @change="applyFilters">
            <option value="">Todas</option>
            <option v-for="uf in ufs" :key="uf" :value="uf">{{ uf }}</option>
          </select>
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportData" class="btn btn-success">
            <i class="fas fa-download"></i>
            Exportar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabela de Candidatos -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando candidatos...</p>
      </div>

      <div v-else-if="candidatos.length === 0" class="empty-state">
        <i class="fas fa-users"></i>
        <h3>Nenhum candidato encontrado</h3>
        <p>Não há candidatos que correspondam aos filtros aplicados.</p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th @click="sortByField('nome')" class="sortable">
                  Nome <i class="fas fa-sort"></i>
                </th>
                <th @click="sortByField('cpf')" class="sortable">
                  CPF <i class="fas fa-sort"></i>
                </th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>UF</th>
                <th @click="sortByField('total_inscricoes')" class="sortable">
                  Inscrições <i class="fas fa-sort"></i>
                </th>
                <th @click="sortByField('status')" class="sortable">
                  Status <i class="fas fa-sort"></i>
                </th>
                <th @click="sortByField('created_at')" class="sortable">
                  Cadastro <i class="fas fa-sort"></i>
                </th>
                <th class="actions-column">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="candidato in candidatos" :key="candidato.id">
                <td>
                  <div class="candidate-info">
                    <strong>{{ candidato.nome }}</strong>
                  </div>
                </td>
                <td>{{ formatCPF(candidato.cpf) }}</td>
                <td>{{ candidato.email }}</td>
                <td>{{ candidato.telefone_celular || '-' }}</td>
                <td>
                  <span class="badge badge-light">{{ candidato.uf }}</span>
                </td>
                <td>
                  <span class="badge badge-primary">{{ candidato.total_inscricoes }}</span>
                  <small class="text-muted">({{ candidato.inscricoes_ativas }} ativas)</small>
                </td>
                <td>
                  <span :class="getStatusClass(candidato.status)">
                    {{ getStatusText(candidato.status) }}
                  </span>
                </td>
                <td>{{ formatDate(candidato.created_at) }}</td>
                <td class="actions">
                  <button 
                    @click="viewDetails(candidato.id)" 
                    class="btn btn-sm btn-info"
                    title="Ver detalhes"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    @click="toggleStatus(candidato)" 
                    :class="candidato.status === 'A' ? 'btn btn-sm btn-warning' : 'btn btn-sm btn-success'"
                    :title="candidato.status === 'A' ? 'Desativar' : 'Ativar'"
                  >
                    <i :class="candidato.status === 'A' ? 'fas fa-ban' : 'fas fa-check'"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="pagination-wrapper">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button @click="changePage(currentPage - 1)" class="page-link">Anterior</button>
              </li>
              <li 
                v-for="page in visiblePages" 
                :key="page" 
                class="page-item" 
                :class="{ active: page === currentPage }"
              >
                <button @click="changePage(page)" class="page-link">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button @click="changePage(currentPage + 1)" class="page-link">Próxima</button>
              </li>
            </ul>
          </nav>
          <div class="pagination-info">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a {{ Math.min(currentPage * perPage, totalItems) }} de {{ totalItems }} candidatos
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CandidatosLista',
  data() {
    return {
      loading: false,
      candidatos: [],
      filters: {
        search: '',
        status: '',
        uf: ''
      },
      sortBy: 'created_at',
      sortOrder: 'desc',
      currentPage: 1,
      perPage: 15,
      totalItems: 0,
      totalPages: 0,
      ufs: ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'],
      searchTimeout: null
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.loadCandidatos()
  },
  methods: {
    async loadCandidatos() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
          ...this.filters
        }

        const response = await axios.get('/ps/candidato/admin/todos', { params })
        this.candidatos = response.data.data
        this.totalItems = response.data.total
        this.totalPages = response.data.last_page
      } catch (error) {
        console.error('Erro ao carregar candidatos:', error)
        this.$sweet.showError('Erro ao carregar candidatos')
      } finally {
        this.loading = false
      }
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.applyFilters()
      }, 500)
    },

    applyFilters() {
      this.currentPage = 1
      this.loadCandidatos()
    },

    clearFilters() {
      this.filters = {
        search: '',
        status: '',
        uf: ''
      }
      this.currentPage = 1
      this.loadCandidatos()
    },

    sortByField(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortOrder = 'asc'
      }
      this.loadCandidatos()
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadCandidatos()
      }
    },

    viewDetails(id) {
      this.$router.push({ 
        name: 'admin-candidato-detalhes', 
        params: { id: id } 
      })
    },

    async toggleStatus(candidato) {
      try {
        const newStatus = candidato.status === 'A' ? 'I' : 'A'
        const action = newStatus === 'A' ? 'ativar' : 'desativar'
        
        const { value } = await this.$sweet.showConfirm(
          'Confirmar Ação',
          `Deseja realmente ${action} o candidato ${candidato.nome}?`
        )

        if (value) {
          await axios.put(`/ps/candidato/admin/${candidato.id}/status`, {
            status: newStatus
          })

          this.$sweet.showSuccess(`Candidato ${action}do com sucesso!`)
          this.loadCandidatos()
        }
      } catch (error) {
        console.error('Erro ao alterar status:', error)
        this.$sweet.showError('Erro ao alterar status do candidato')
      }
    },

    async exportData() {
      try {
        this.$sweet.showInfo('Gerando arquivo Excel...')
        
        const response = await axios.get('/ps/candidato/admin/exportar-excel', {
          params: this.filters,
          responseType: 'blob'
        })
        
        // Criar download do arquivo
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `candidatos_${new Date().getTime()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$sweet.showSuccess('Arquivo exportado com sucesso!')
      } catch (error) {
        console.error('Erro ao exportar dados:', error)
        this.$sweet.showError('Erro ao exportar dados')
      }
    },

    formatCPF(cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },

    formatDate(date) {
      if (!date) return '-'
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split('-')
          return `${day}/${month}/${year}`
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof date === 'string') {
          let dateString = date.replace('Z', '')
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ')
            const [year, month, day] = datePart.split('-')
            return `${day}/${month}/${year}`
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            // Remover o 'Z' do final se existir e tratar como hora local
            dateString = dateString.replace('Z', '')
            // Se tiver apenas data e hora sem timezone, usar diretamente
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T')
              const [year, month, day] = datePart.split('-')
              // Retornar apenas a data, ignorando a hora
              return `${day}/${month}/${year}`
            }
          }
        }
        
        // Criar a data e formatar no fuso horário local
        const dateObj = new Date(date)
        
        // Verificar se a data é válida
        if (isNaN(dateObj.getTime())) return '-'
        
        // Usar métodos locais para evitar conversão UTC
        const day = String(dateObj.getDate()).padStart(2, '0')
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const year = dateObj.getFullYear()
        
        return `${day}/${month}/${year}`
      } catch (error) {
        console.error('Erro ao formatar data:', error)
        return '-'
      }
    },

    getStatusClass(status) {
      return {
        'badge badge-success': status === 'A',
        'badge badge-secondary': status === 'I'
      }
    },

    getStatusText(status) {
      return status === 'A' ? 'Ativo' : 'Inativo'
    }
  }
}
</script>

<style lang="scss" scoped>
.candidatos-lista {
  .page-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .page-title {
      margin: 0 0 8px 0;
      font-size: 24px;
      font-weight: 600;
      color: #2c3e50;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
        color: #667eea;
      }
    }

    .page-subtitle {
      margin: 0;
      font-size: 14px;
      color: #6c757d;
    }
  }

  .filters-card {
    background: white;
    border-radius: 15px;
    padding: 20px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .filters-row {
      display: flex;
      gap: 20px;
      align-items: end;
      flex-wrap: wrap;

      .filter-group {
        flex: 1;
        min-width: 200px;

        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }

        .form-control {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 14px;
          transition: border-color 0.3s ease;

          &:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
        }
      }

      .filter-actions {
        display: flex;
        gap: 10px;
        margin-left: auto;

        .btn {
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;

          &.btn-secondary {
            background: #6c757d;
            border-color: #6c757d;
            color: white;

            &:hover {
              background: #5a6268;
              border-color: #5a6268;
            }
          }

          &.btn-success {
            background: #28a745;
            border-color: #28a745;
            color: white;

            &:hover {
              background: #218838;
              border-color: #1e7e34;
            }
          }
        }
      }
    }
  }

  .table-card {
    background: white;
    border-radius: 15px;
    padding: 0;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;

    .loading-state, .empty-state {
      padding: 60px 30px;
      text-align: center;

      i {
        font-size: 48px;
        color: #667eea;
        margin-bottom: 15px;
        opacity: 0.7;
      }

      p, h3 {
        margin: 0;
        color: #6c757d;
      }

      h3 {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #495057;
      }
    }

    .table-responsive {
      overflow-x: auto;
    }

    .table {
      margin: 0;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        background: #f8f9fa;

        th {
          padding: 15px 12px;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #e9ecef;
          font-size: 14px;
          white-space: nowrap;

          &.sortable {
            cursor: pointer;
            user-select: none;
            transition: background-color 0.3s ease;

            &:hover {
              background: #e9ecef;
            }

            i {
              margin-left: 5px;
              opacity: 0.5;
            }
          }
        }
      }

      tbody {
        tr {
          transition: background-color 0.3s ease;

          &:hover {
            background: #f8f9fa;
          }

          td {
            padding: 12px;
            border-bottom: 1px solid #e9ecef;
            vertical-align: middle;
            font-size: 14px;

            .candidate-info {
              strong {
                color: #2c3e50;
              }
            }

            .badge {
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 12px;

              &.badge-success {
                background: #28a745;
                color: white;
              }

              &.badge-secondary {
                background: #6c757d;
                color: white;
              }

              &.badge-primary {
                background: #667eea;
                color: white;
              }

              &.badge-light {
                background: #f8f9fa;
                color: #495057;
                border: 1px solid #e9ecef;
              }
            }

            &.actions {
              text-align: center;
              white-space: nowrap;

              .btn {
                margin: 0 2px;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 12px;
                border: none;
                transition: all 0.3s ease;

                &.btn-info {
                  background: #17a2b8;
                  color: white;

                  &:hover {
                    background: #138496;
                  }
                }

                &.btn-warning {
                  background: #ffc107;
                  color: #212529;

                  &:hover {
                    background: #e0a800;
                  }
                }

                &.btn-success {
                  background: #28a745;
                  color: white;

                  &:hover {
                    background: #218838;
                  }
                }
              }
            }
          }
        }
      }
    }

    .pagination-wrapper {
      padding: 20px 30px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f9fa;

      .pagination {
        margin: 0;
        display: flex;
        gap: 5px;

        .page-item {
          .page-link {
            padding: 8px 12px;
            border: 1px solid #e9ecef;
            color: #667eea;
            background: white;
            border-radius: 6px;
            transition: all 0.3s ease;

            &:hover {
              background: #667eea;
              color: white;
              border-color: #667eea;
            }
          }

          &.active .page-link {
            background: #667eea;
            color: white;
            border-color: #667eea;
          }

          &.disabled .page-link {
            color: #6c757d;
            background: #f8f9fa;
            cursor: not-allowed;

            &:hover {
              background: #f8f9fa;
              color: #6c757d;
              border-color: #e9ecef;
            }
          }
        }
      }

      .pagination-info {
        font-size: 14px;
        color: #6c757d;
      }
    }
  }
}

// Responsividade
@media (max-width: 768px) {
  .candidatos-lista {
    .filters-card .filters-row {
      flex-direction: column;
      align-items: stretch;

      .filter-actions {
        margin-left: 0;
        justify-content: center;
      }
    }

    .table-card .pagination-wrapper {
      flex-direction: column;
      gap: 15px;
      text-align: center;
    }
  }
}
</style>
